import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useLiveChat } from '../contexts/LiveChatContext';
import { useSports, useLeagues } from '../services/sportsService';
import { Sport, getSportIcon } from '../types/sports';
import SportIcon from './sports/SportIcon';
import LanguageSelector from './LanguageSelector';
import { SidebarListPlaceholder } from './Skeleton';

// Route to category mapping (defined outside component to avoid recreating on each render)
const ROUTE_TO_CATEGORY_MAP: Record<string, string> = {
  '/': '',
  '/sports': 'sports',
  '/live-sports': 'live-sports',
  '/esports': 'esports',
  '/live-esports': 'live-esports',
  '/casino': 'live-casino',
  '/slot': 'slot',
  '/roulette': 'roulette',
  '/bet-history': 'bet-history',
  '/event': 'event',
  '/question': 'question',
  '/rule': 'notice',
  '/my-page': 'my-page',
  '/deposit': 'wallet',
  '/withdraw': 'wallet',
  '/wallet': 'wallet',
  '/message': 'message',
  '/daily-check': 'daily-check',
  '/coupon': 'coupon',
  '/bonus': 'bonus',
  '/blog': 'blog',
  '/catchcoin': 'catchcoin',
  '/minigame': 'btcgraph',
  '/minigame/builtin': 'btcgraph',
  '/minigame/free': 'btcgraph',
  '/minigame/btcgraph': 'btcgraph',
};
// Any path under /minigame (e.g. /minigame/game/slug) should highlight minigame
const isMinigamePath = (pathname: string) => pathname === '/minigame' || pathname.startsWith('/minigame/');

interface SidebarProps {
  onScrollToSection?: (sectionId: string) => void;
  activeCategory: string;
  onSetActiveCategory: (category: string) => void;
  onClearHeaderActive: () => void;
  onCloseSidebar?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onScrollToSection, activeCategory, onSetActiveCategory, onClearHeaderActive, onCloseSidebar }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useSearchParams()[0];
  const { requireAuth } = useRequireAuth();
  const { isOpen: isChatOpen, toggleChat, onlineCount, isConnected } = useLiveChat();
  const [expandedSportsBetting, setExpandedSportsBetting] = useState<string | null>(null); // Track which sports betting section is expanded
  const [expandedMinigame, setExpandedMinigame] = useState<boolean>(false);
  const manualExpansionRef = useRef<boolean>(false); // Track if expansion was manual (user click)
  const lastManualExpansionRef = useRef<string | null>(null); // Track last manually expanded button
  const userDidCollapseRef = useRef<boolean>(false); // True only when user explicitly collapsed (so we don't re-expand on refresh)
  const isNavigatingRef = useRef<boolean>(false); // Track if navigation is in progress
  
  // Determine if we're on a sports betting page
  const isSportsPage = ['sports', 'live-sports', 'esports', 'live-esports'].includes(activeCategory);
  // Event detail (/event/:fixtureId) is part of sports flow — keep sidebar expand state when navigating to it
  const isEventDetailPage = location.pathname.startsWith('/event/');
  const sportType = ['sports', 'live-sports'].includes(activeCategory) ? 'sport' : 
                    ['esports', 'live-esports'].includes(activeCategory) ? 'esport' : undefined;
  
  // Get selected sport and league from URL
  const selectedSportParam = searchParams.get('sport');
  // const selectedLeagueParam = searchParams.get('league');
  const selectedSport = selectedSportParam ? parseInt(selectedSportParam) : null;
  
  // Sports betting buttons - each is expandable
  const sportsBettingButtons = [
    {
      id: 'sports',
      name: t('sports.prematch'),
      path: '/sports',
      icon: <img src="/prematch.png" alt="" width={16} height={16} className="w-4 h-4 object-contain inline-block align-middle" />,
      sportType: 'sport' as const,
    },
    {
      id: 'live-sports',
      name: t('sports.live'),
      path: '/live-sports',
      icon: <img src="/live.png" alt="" width={16} height={16} className="w-4 h-4 object-contain inline-block align-middle" />,
      sportType: 'sport' as const,
    },
    // {
    //   id: 'esports',
    //   name: t('esports.prematch'),
    //   path: '/esports',
    //   icon: <img src="/prematch-esports.png" alt="" className="w-4 h-4 object-contain inline-block align-middle" />,
    //   sportType: 'esport' as const,
    // },
    // {
    //   id: 'live-esports',
    //   name: t('esports.live'),
    //   path: '/live-esports',
    //   icon: <img src="/live-esports.png" alt="" className="w-4 h-4 object-contain inline-block align-middle" />,
    //   sportType: 'esport' as const,
    // },
  ];
  
  // Determine which sport type to fetch based on expanded button
  const expandedButton = sportsBettingButtons.find(b => b.id === expandedSportsBetting);
  const currentSportType = expandedButton?.sportType || sportType || 'sport';
  
  // Fetch sports data when a sports betting section is expanded
  const { data: sports = [], isLoading: sportsLoading } = useSports({ 
    sportType: currentSportType
  });
  useLeagues({ 
    sportId: selectedSport || undefined, 
    sportType: currentSportType
  });
  
  const toggleSportsBetting = (buttonId: string) => {
    // Prevent rapid clicks while navigation is in progress (but allow collapse)
    if (isNavigatingRef.current && expandedSportsBetting !== buttonId) {
      return;
    }
    
    const button = sportsBettingButtons.find(b => b.id === buttonId);
    if (!button) return;
    
    // If clicking the same button that's already expanded, collapse it
    if (expandedSportsBetting === buttonId) {
      // Mark as manual collapse so we don't re-expand until user navigates away or expands again
      manualExpansionRef.current = true;
      lastManualExpansionRef.current = null; // Clear last manual expansion
      userDidCollapseRef.current = true;
      setExpandedSportsBetting(null);
      
      // Reset flags after a delay to allow collapse to complete
      setTimeout(() => {
        isNavigatingRef.current = false;
        manualExpansionRef.current = false;
      }, 300);
      return;
    }
    
    // Mark as manual expansion to prevent useEffect from overriding
    manualExpansionRef.current = true;
    lastManualExpansionRef.current = buttonId;
    userDidCollapseRef.current = false; // User is expanding, clear collapse flag
    isNavigatingRef.current = true;
    
    setExpandedSportsBetting(buttonId);
    // Navigate to the page when expanding and set active category
    onClearHeaderActive(); // Clear header active state
    
    // Navigate first, then set active category after a small delay to prevent race condition
    navigate(button.path);
    
    // Use requestAnimationFrame to ensure navigation starts before setting category
    requestAnimationFrame(() => {
      onSetActiveCategory(buttonId);
      onCloseSidebar?.(); // Close sidebar on mobile
    });
    
    // Reset navigation flag after navigation completes
    // Keep manual expansion flag longer to prevent useEffect from interfering
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 200);
    
    // Keep manual expansion flag set longer to prevent useEffect from overriding
    setTimeout(() => {
      manualExpansionRef.current = false;
    }, 1000);
  };
  
  // Derive which section to expand from pathname (more reliable than activeCategory)
  const eventType = searchParams.get('type');
  const pathnameToExpand = location.pathname === '/live-sports' ? 'live-sports'
    : location.pathname === '/sports' ? 'sports'
    : location.pathname === '/esports' ? 'esports'
    : location.pathname === '/live-esports' ? 'live-esports'
    : location.pathname.startsWith('/event/') ? (eventType === 'live' ? 'live-sports' : 'sports')
    : null;

  // Auto-expand the relevant sports betting section when on that page
  // But don't override manual expansions (user clicks) or collapses
  useEffect(() => {
    // Skip if navigation is in progress or manual expansion is active
    if (isNavigatingRef.current || manualExpansionRef.current) {
      return;
    }
    
    // Don't auto-expand only when user explicitly collapsed (not on refresh: then userDidCollapseRef is false)
    if ((isSportsPage || pathnameToExpand) && userDidCollapseRef.current && expandedSportsBetting === null) {
      return;
    }
    
    if (pathnameToExpand) {
      // On sports/live-sports/etc page - expand the matching section
      if (expandedSportsBetting !== pathnameToExpand) {
        setExpandedSportsBetting(pathnameToExpand);
      }
    } else if (isEventDetailPage && expandedSportsBetting === null) {
      // On event detail without expanded section - auto-expand so user can click sports
      setExpandedSportsBetting('sports');
    } else if (!isEventDetailPage) {
      // If we navigate away from sports (and not to event detail), collapse and clear flags
      setExpandedSportsBetting(null);
      lastManualExpansionRef.current = null;
      manualExpansionRef.current = false;
      userDidCollapseRef.current = false;
    }
  }, [pathnameToExpand, isEventDetailPage, expandedSportsBetting, isSportsPage]);

  // Auto-expand Minigame when on any minigame route
  useEffect(() => {
    if (isMinigamePath(location.pathname)) {
      setExpandedMinigame(true);
    } else {
      setExpandedMinigame(false);
    }
  }, [location.pathname]);

  // Games section - Core gaming options (viewable without login)
  const gamesNavItems = [
    // {
    //   id: 'live-casino',
    //   name: t('navigation.liveCasino'),
    //   type: 'navigate',
    //   path: '/casino',
    //   icon: <img src="/casino.png" alt="" width={16} height={16} className="w-4 h-4 object-contain inline-block align-middle" />,
    //   noAuth: true,
    // },
    {
      id: 'slot',
      name: t('navigation.slotGames'),
      type: 'navigate',
      path: '/slot',
      icon: <img src="/slot.png" alt="" width={16} height={16} className="w-4 h-4 object-contain inline-block align-middle" />,
      noAuth: true,
    },
  ];

  // Minigame sub-items (Built-in, Free Game) - same pattern as Sports: first is default on expand
  const minigameSubItems = [
    { id: 'minigame-builtin', name: t('minigame.builtIn', 'Built-in Games'), path: '/minigame/builtin', noAuth: true },
    { id: 'minigame-free', name: t('minigame.freeGames', 'Free Games'), path: '/minigame/free', noAuth: true },
  ];

  const toggleMinigame = () => {
    if (expandedMinigame) {
      setExpandedMinigame(false);
      return;
    }
    setExpandedMinigame(true);
    onClearHeaderActive();
    navigate('/minigame/builtin'); // First sub-button auto focus (same as Sports)
    requestAnimationFrame(() => {
      onSetActiveCategory('btcgraph');
      onCloseSidebar?.();
    });
  };
  
  const getSportName = (sport: Sport) => {
    return i18n.language === 'ko' ? sport.name_kor : sport.name;
  };
  
  // const getLeagueName = (league: League) => {
  //   return i18n.language === 'ko' ? (league.name_kor || league.name) : league.name;
  // };
  
  // Promotions & Rewards section (requires auth)
  const promotionsNavItems = [
    // { id: 'event', name: t('navigation.event'), type: 'navigate', path: '/event', icon: '🎉' },
    // { id: 'coupon', name: t('header.coupon'), type: 'navigate', path: '/coupon', icon: '🎁' },
    { id: 'bonus', name: t('navigation.bonus', 'Bonus'), type: 'navigate', path: '/bonus', icon: '🎰' },
    { id: 'daily-check', name: t('header.dailyCheck'), type: 'navigate', path: '/daily-check', icon: '📅' },
  ];

  // Wallet & Finance section (requires auth)
  const walletNavItems = [
    { id: 'wallet', name: t('header.wallet') || 'Wallet', type: 'navigate', path: '/wallet', icon: '💎' },
    // { id: 'catchcoin', name: 'CatchCoin', type: 'navigate', path: '/catchcoin', icon: '🪙' },
  ];

  // Account section (requires auth)
  const accountNavItems = [
    { id: 'my-page', name: t('navigation.myPage'), type: 'navigate', path: '/my-page', icon: '👤' },
    { id: 'bet-history', name: t('navigation.betHistory'), type: 'navigate', path: '/bet-history', icon: '📜' },
    { id: 'message', name: t('header.message'), type: 'navigate', path: '/message', icon: '✉️' },
  ];

  // Information section (public pages)
  const infoNavItems = [
    { id: 'notice', name: t('navigation.about'), type: 'navigate', path: '/rule', icon: '📋', noAuth: true },
  ];

  const handleNavClick = (item: any) => {
    const performNavigation = () => {
      onClearHeaderActive(); // Clear header active state when sidebar button is clicked
      
      switch (item.type) {
        case 'navigate':
          if (item.path) {
            navigate(item.path);
            // Don't set active category here - let useEffect handle it based on route
            // This prevents the flickering issue
            onCloseSidebar?.();
          }
          break;
          
        case 'scroll':
          if (onScrollToSection) {
            onScrollToSection(item.id);
            onSetActiveCategory(item.id); // Only set for scroll since there's no route change
            onCloseSidebar?.();
          }
          break;
          
        default:
          if (item.path) {
            navigate(item.path);
            onCloseSidebar?.();
          }
          break;
      }
    };

    // If item doesn't require auth (noAuth: true), navigate directly
    // Otherwise, check authentication first
    if (item.noAuth) {
      performNavigation();
    } else {
      requireAuth(performNavigation);
    }
  };


  // Determine active category based on current route
  React.useEffect(() => {
    let category = ROUTE_TO_CATEGORY_MAP[location.pathname];
    // Event detail (/event/:fixtureId) - use type param for prematch vs live
    if (category === undefined && location.pathname.startsWith('/event/')) {
      category = searchParams.get('type') === 'live' ? 'live-sports' : 'sports';
    }
    // Any minigame path (e.g. /minigame, /minigame/btcgraph, /minigame/game/slug)
    if (category === undefined && isMinigamePath(location.pathname)) {
      category = 'btcgraph';
    }
    // Only update if we have a mapped category, otherwise clear it
    onSetActiveCategory(category !== undefined ? category : '');
  }, [location.pathname, searchParams, onSetActiveCategory]);

  // Render navigation section helper
  const renderNavSection = (items: any[], sectionTitle: string, showBorder: boolean = true) => (
    <nav className={`p-3 lg:p-4 ${showBorder ? 'border-t border-white/5' : ''}`}>
      <h3 className="text-gray-500 uppercase text-[10px] font-bold mb-3 tracking-widest px-3">{sectionTitle}</h3>
      <div className="space-y-1">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={() => handleNavClick(item)}
            className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm group ${
              activeCategory === item.id
                ? 'bg-gradient-to-r from-primary-600/20 to-primary-500/10 text-primary-400 border-l-2 border-primary-500'
                : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
          >
            <span className={`text-base w-5 flex-shrink-0 text-center transition-transform duration-200 ${activeCategory === item.id ? '' : 'group-hover:scale-110'}`}>{item.icon}</span>
            <span className="font-medium whitespace-nowrap min-w-0 overflow-hidden text-ellipsis">{item.name}</span>
            {activeCategory === item.id && (
              <div className="ml-auto w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
            )}
          </button>
        ))}
      </div>
    </nav>
  );

  return (
    <aside className={`w-64 shrink-0 bg-dark-800/95 backdrop-blur-xl pt-16 h-screen overflow-hidden lg:rounded-none flex flex-col border-r border-white/5 relative z-[999]`}>
      {/* Scrollable Navigation Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
        {/* Games Section */}
        <nav className="p-3 lg:p-4">
          <h3 className="text-gray-500 uppercase text-[10px] font-bold mb-3 tracking-widest px-3">{t('navigation.sectionGames')}</h3>
          <div className="space-y-1">
            {/* Sports Betting Buttons - Each is expandable */}
            {sportsBettingButtons.map((button) => {
              const isExpanded = expandedSportsBetting === button.id;
              const isActive = location.pathname === button.path
                || (location.pathname.startsWith('/event/') && eventType === 'live' && button.id === 'live-sports')
                || (location.pathname.startsWith('/event/') && (eventType !== 'live' || !eventType) && button.id === 'sports')
                || activeCategory === button.id;
              
              return (
                <div key={button.id}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSportsBetting(button.id);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 text-sm group ${
                      isActive
                        ? 'bg-gradient-to-r from-primary-600/20 to-primary-500/10 text-primary-400 border-l-2 border-primary-500'
                        : 'text-gray-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <span className={`text-base w-5 flex-shrink-0 text-center transition-transform duration-200 ${isActive ? '' : 'group-hover:scale-110'}`}>{button.icon}</span>
                      <span className="font-medium whitespace-nowrap min-w-0 overflow-hidden text-ellipsis">{button.name}</span>
                    </div>
                    <svg 
                      className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Sports List - Show when this button is expanded */}
                  {isExpanded && (
                    <div 
                      className="mt-2 ml-4 border-l-2 border-white/5 pl-2 max-h-[400px] overflow-y-auto custom-scrollbar"
                      onClick={(e) => {
                        // Stop propagation to prevent parent button from toggling
                        e.stopPropagation();
                      }}
                    >
                  {/* Sports List */}
                  {sportsLoading ? (
                    <SidebarListPlaceholder />
                  ) : (Array.isArray(sports) && sports.length > 0) ? (
                    <div className="space-y-0.5">
                      {sports.map((sport: Sport, idx) => {
                        const sportId = sport.oddsId ?? (sport as any).odds_id;
                        if (sportId === null || sportId === undefined) return <React.Fragment key={sport.Id ?? `sport-${idx}`} />;
                        const isSelectedSport = selectedSport != null && (selectedSport === sportId || selectedSport === Number(sportId));
                        const targetPath = expandedSportsBetting
                          ? (sportsBettingButtons.find(b => b.id === expandedSportsBetting)?.path ?? '/sports')
                          : (isSportsPage && activeCategory && !location.pathname.startsWith('/event/')
                              ? location.pathname
                              : '/sports');
                        const sportUrl = `${targetPath}?sport=${sportId}`;

                        return (
                          <Link
                            key={sport.Id ?? (sport as any).id ?? `sport-${sportId}`}
                            to={sportUrl}
                            onClick={(e) => {
                              e.stopPropagation();
                              onSetActiveCategory(expandedSportsBetting ?? (isSportsPage ? activeCategory : 'sports') ?? 'sports');
                              onCloseSidebar?.();
                            }}
                            className={`w-full flex items-center gap-1.5 px-2 py-1.5 transition-all text-xs rounded cursor-pointer ${
                              isSelectedSport
                                ? 'bg-gradient-to-r from-primary-500/10 to-primary-600/5 text-primary-400'
                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                            }`}
                            style={{ pointerEvents: 'auto', zIndex: 10 }}
                          >
                            <SportIcon icon={getSportIcon(sportId, sport.name)} size="sm" />
                            <span className="truncate">{getSportName(sport)}</span>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-2 text-center text-gray-500 text-[10px]">
                      <p>{t('sports.noSports')}</p>
                    </div>
                  )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Other Games */}
            {gamesNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm group ${
                  activeCategory === item.id
                    ? 'bg-gradient-to-r from-primary-600/20 to-primary-500/10 text-primary-400 border-l-2 border-primary-500'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <span className={`text-base w-5 flex-shrink-0 text-center transition-transform duration-200 ${activeCategory === item.id ? '' : 'group-hover:scale-110'}`}>{item.icon}</span>
                <span className="font-medium whitespace-nowrap min-w-0 overflow-hidden text-ellipsis">{item.name}</span>
                {activeCategory === item.id && (
                  <div className="ml-auto flex-shrink-0 w-1.5 h-1.5 bg-primary-500 rounded-full"></div>
                )}
              </button>
            ))}

            {/* Minigame - expandable with Built-in and Free Game (same pattern as Sports) */}
            <div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMinigame();
                }}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 text-sm group ${
                  activeCategory === 'btcgraph'
                    ? 'bg-gradient-to-r from-primary-600/20 to-primary-500/10 text-primary-400 border-l-2 border-primary-500'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white'
                }`}
              >
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <span className={`text-base w-5 flex-shrink-0 text-center transition-transform duration-200 ${activeCategory === 'btcgraph' ? '' : 'group-hover:scale-110'}`}>📈</span>
                  <span className="font-medium whitespace-nowrap min-w-0 overflow-hidden text-ellipsis">{t('navigation.minigame')}</span>
                </div>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${expandedMinigame ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {expandedMinigame && (
                <div
                  className="mt-2 ml-4 border-l-2 border-white/5 pl-2 space-y-0.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  {minigameSubItems.map((sub) => {
                    const isActiveSub = location.pathname === sub.path;
                    return (
                      <Link
                        key={sub.id}
                        to={sub.path}
                        onClick={() => {
                          onSetActiveCategory('btcgraph');
                          onCloseSidebar?.();
                        }}
                        className={`w-full flex items-center px-2 py-1.5 rounded-lg text-xs transition-all text-left ${
                          isActiveSub
                            ? 'bg-gradient-to-r from-primary-500/10 to-primary-600/5 text-primary-400'
                            : 'text-gray-400 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        {sub.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </nav>

        {/* Live Chat Button */}
        <div className="p-3 lg:p-4 border-t border-white/5">
          <button
            onClick={() => {
              toggleChat();
              onCloseSidebar?.();
            }}
            className={`w-full flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 group ${
              isChatOpen
                ? 'bg-gradient-to-r from-primary-600/20 to-emerald-500/20 border border-primary-500/30'
                : 'bg-gradient-to-r from-primary-500/10 to-emerald-500/10 hover:from-primary-500/20 hover:to-emerald-500/20 border border-white/5 hover:border-primary-500/20'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  isChatOpen 
                    ? 'bg-gradient-to-br from-primary-500 to-emerald-500' 
                    : 'bg-gradient-to-br from-primary-500/50 to-emerald-500/50 group-hover:from-primary-500 group-hover:to-emerald-500'
                } transition-all duration-200`}>
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                {isConnected && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-dark-800 animate-pulse"></div>
                )}
              </div>
              <div className="text-left">
                <div className={`font-semibold text-sm ${isChatOpen ? 'text-primary-400' : 'text-gray-300 group-hover:text-white'} transition-colors`}>
                  {t('liveChat.title') || 'Live Chat'}
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <span className="text-[11px] text-gray-500">
                    {onlineCount} {t('liveChat.online') || 'online'}
                  </span>
                </div>
              </div>
            </div>
            <svg className={`w-4 h-4 transition-transform duration-200 ${isChatOpen ? 'text-primary-400 rotate-180' : 'text-gray-500 group-hover:text-white'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Promotions & Rewards Section */}
        {renderNavSection(promotionsNavItems, t('navigation.sectionPromotions'))}

        {/* Wallet & Finance Section */}
        {renderNavSection(walletNavItems, t('navigation.sectionWallet'))}

        {/* Account Section */}
        {renderNavSection(accountNavItems, t('navigation.sectionAccount'))}

        {/* Information Section */}
        {renderNavSection(infoNavItems, t('navigation.sectionInfo'))}

        {/* Language Selector */}
        <div className="p-3 lg:p-4 border-t border-white/5">
          <h3 className="text-gray-500 uppercase text-[10px] font-bold mb-3 tracking-widest px-3">{t('navigation.language') || 'Language'}</h3>
            <LanguageSelector variant="sidebar" />
        </div>

        {/* Footer Branding */}
        <div className="p-4 mt-auto">
          <div className="text-center text-gray-600 text-[10px]">
            Copyright © {new Date().getFullYear()} ApexBet.
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar; 